/**
 * An alarm rule's threshold in the org's units (units slice 2b, docs/plans/2026-10-06_units-slice-2.md section 4). A rule
 * watches one tag and holds its threshold as a bare number in that tag's own unit: the rule has no unit of its own. The
 * editor reads and writes the number in the org's shown unit, and the rule is saved in the tag's unit; a threshold the
 * person did not touch is sent exactly as it was stored. Where the choice converts nothing (metric, a unit it shows as
 * recorded, a tag with no unit), every function here is the plain number as before. Pure.
 */
import { shownFor, typedToStored } from "@/lib/units/display";
import type { DisplayPrefs } from "@/lib/units/prefs";
import { formNumber } from "@/lib/brief/limits";

/** What the form held when it opened: the stored threshold, and the text it showed for it. */
export interface OpenedThreshold {
  value: number;
  text: string;
}

/**
 * The threshold as the form shows it: as stored where nothing converts (the whole number, as it always read); in the
 * shown unit, to at most four decimals, where it does.
 */
export function thresholdFormText(value: number, storedUnit: string, prefs: DisplayPrefs | undefined): string {
  const shown = shownFor(storedUnit, prefs);
  return shown.converted ? formNumber(shown.convert(value)) : String(value);
}

/** The field's label: "Value (°F)", the unit the threshold is read in; "Value" for a tag with no unit. */
export function thresholdLabel(storedUnit: string, prefs: DisplayPrefs | undefined): string {
  const unit = shownFor(storedUnit, prefs).unit;
  return unit ? `Value (${unit})` : "Value";
}

/**
 * The number a save sends, in the tag's unit: the stored threshold as it was while the field still shows what it opened
 * with, else what was typed, converted back from the shown unit. Null when the text is not a number.
 */
export function thresholdSaved(a: {
  text: string;
  storedUnit: string;
  prefs: DisplayPrefs | undefined;
  opened?: OpenedThreshold;
}): number | null {
  const t = a.text.trim();
  if (t === "") return null;
  const typed = Number(t);
  if (!Number.isFinite(typed)) return null;
  if (a.opened && t === a.opened.text.trim()) return a.opened.value;
  return typedToStored(typed, shownFor(a.storedUnit, a.prefs).unit, a.storedUnit);
}

/**
 * The field's text when the rule's tag changes. A threshold left as it opened (a suggestion, held in the tag's unit, or
 * the rule's own) is shown again in the new tag's shown unit, so it is still saved exactly as it opened; a number the
 * person typed stays as typed (#1190 review MEDIUM 3: a suggestion that came before its tag was picked).
 */
export function thresholdRetargeted(a: {
  text: string;
  /** The threshold the field opened with, in the tag's unit; undefined when it opened empty. */
  opened: number | undefined;
  fromUnit: string;
  toUnit: string;
  prefs: DisplayPrefs | undefined;
}): string {
  if (a.opened === undefined || !Number.isFinite(a.opened) || a.text.trim() === "") return a.text;
  if (Number(a.text) !== Number(thresholdFormText(a.opened, a.fromUnit, a.prefs))) return a.text;
  return thresholdFormText(a.opened, a.toUnit, a.prefs);
}

/**
 * A rule's condition in words: "greater than 1500 for 30s". An org whose choice converted the threshold reads it with
 * its shown unit ("greater than 2732 °F for 30s"); anywhere else it is the stored number, as it always was.
 */
export function thresholdConditionText(
  op: string,
  c: { value: number | string; forSeconds?: number },
  storedUnit: string,
  prefs: DisplayPrefs | undefined,
): string {
  const forStr = c.forSeconds ? ` for ${c.forSeconds}s` : "";
  const n = typeof c.value === "number" ? c.value : Number(c.value);
  const shown = shownFor(storedUnit, prefs);
  if (!shown.converted || !Number.isFinite(n)) return `${op} ${c.value}${forStr}`;
  return `${op} ${formNumber(shown.convert(n))} ${shown.unit}${forStr}`;
}
